/*=========================================================================================
  File Name: actions.js
  Namespace: certification
  Description: Vuex Store - Certification actions
==========================================================================================*/

import {
  getCertificationExam,
  getExamByCertificationVersion,
  getCertificationVersionWithCertification,
  listCertificationVersions,
  getCertificationHistory,
  getCertificationChangeNotes,
  createCertificationExam,
  createCertificationVersion,
  createCertificationOrchestrator,
  updateCertification,
  updateCertificationVersion,
  uploadCertificationBadge,
  syncCertificationVersionStatus,
  updateCertificationExam,
  uploadCertificationExamCsv,
  validateCertificationExamCsv,
  downloadCertificationExamCsv,
  downloadCertificationExamTemplateCsv,
  getLaunchConfiguration,
  updateLaunchConfiguration,
  updateLaunchSellability,
} from '@/services/certifications.service'

let certificationHistoryRequestToken = 0
let certificationVersionRequestToken = 0
let certificationExamRequestToken = 0
let certificationExamByVersionRequestToken = 0
let launchConfigurationRequestToken = 0

const actions = {
  async listCertificationVersionsAction({ commit }, params = {}) {
    const response = await listCertificationVersions(params)
    commit('STORE_CERTIFICATION_VERSIONS', response)
    return response
  },
  async getCertificationHistoryAction(
    { commit, state },
    { certificationId, includeAll }
  ) {
    const requestToken = ++certificationHistoryRequestToken
    commit('SET_CERTIFICATION_HISTORY_REQUEST', requestToken)
    commit('STORE_CERTIFICATION_HISTORY', {
      certificationId,
      history: [],
    })

    const response = await getCertificationHistory({
      certificationId,
      includeAll,
    })

    if (state.certificationHistoryRequestToken !== requestToken) {
      return response
    }

    commit('STORE_CERTIFICATION_HISTORY', {
      certificationId,
      history: response.results,
      certification: response.certification,
    })
    return response
  },
  async getCertificationChangeNotesAction(_, params = {}) {
    return getCertificationChangeNotes(params)
  },
  async createCertificationExamAction(_, { sourceVersionId }) {
    const response = await createCertificationExam({ sourceVersionId })
    return response
  },
  async createCertificationVersionAction(_, params = {}) {
    const response = await createCertificationVersion(params)
    return response
  },
  async createCertificationOrchestratorAction({ commit }, payload) {
    const response = await createCertificationOrchestrator(payload)
    commit('STORE_CERTIFICATION_VERSION', {
      ...response.version,
      certification: response.certification,
    })
    commit('STORE_CERTIFICATION_EXAM', response.exam)
    return response
  },
  async updateCertificationVersionAction(_, params = {}) {
    const response = await updateCertificationVersion(params)
    return response
  },
  async updateCertificationAction({ commit, state }, params = {}) {
    const response = await updateCertification(params)
    if (
      state.certificationVersion &&
      state.certificationVersion.certification &&
      state.certificationVersion.certification.id === response.id
    ) {
      commit('STORE_CERTIFICATION', response)
    }
    return response
  },
  async uploadCertificationBadgeAction({ commit, state }, params = {}) {
    const response = await uploadCertificationBadge(params)
    const certificationVersion = state.certificationVersion || null
    const activeCertification =
      certificationVersion && certificationVersion.certification

    if (
      activeCertification &&
      activeCertification.id === params.certificationId
    ) {
      commit('STORE_CERTIFICATION', {
        ...activeCertification,
        ...response,
      })
    }
    return response
  },
  async syncCertificationVersionStatusAction(_, params = {}) {
    const response = await syncCertificationVersionStatus(params)
    return response
  },
  async getCertificationVersionWithCertificationAction(
    { commit, state },
    params = {}
  ) {
    const requestToken = ++certificationVersionRequestToken
    commit('SET_CERTIFICATION_VERSION_REQUEST', requestToken)
    const response = await getCertificationVersionWithCertification(params)
    if (state.certificationVersionRequestToken !== requestToken) {
      return response
    }
    commit('STORE_CERTIFICATION_VERSION', response)
    return response
  },
  async getCertificationExamAction({ commit, state }, params = {}) {
    const requestToken = ++certificationExamRequestToken
    commit('SET_CERTIFICATION_EXAM_REQUEST', requestToken)
    const response = await getCertificationExam(params)
    if (state.certificationExamRequestToken !== requestToken) {
      return response
    }
    commit('STORE_CERTIFICATION_EXAM', response)
    return response
  },
  async updateCertificationExamAction(_, params = {}) {
    const response = await updateCertificationExam(params)
    return response
  },
  async uploadCertificationExamCsvAction(_, params = {}) {
    const response = await uploadCertificationExamCsv(params)
    return response
  },
  async validateCertificationExamCsvAction(_, params = {}) {
    const response = await validateCertificationExamCsv(params)
    return response
  },
  async downloadCertificationExamCsvAction(_, params = {}) {
    const response = await downloadCertificationExamCsv(params)
    return response
  },
  async downloadCertificationExamTemplateCsvAction(_, params = {}) {
    const response = await downloadCertificationExamTemplateCsv(params)
    return response
  },
  async getExamByCertificationVersionAction({ commit, state }, params = {}) {
    const requestToken = ++certificationExamByVersionRequestToken
    commit('SET_CERTIFICATION_EXAM_BY_VERSION_REQUEST', requestToken)
    commit('STORE_CERTIFICATION_EXAM', null)
    try {
      const response = await getExamByCertificationVersion(params)
      const exam = response && response.id ? response : null
      if (state.certificationExamByVersionRequestToken !== requestToken) {
        return exam
      }
      commit('STORE_CERTIFICATION_EXAM', exam)
      return exam
    } catch (error) {
      if (error && error.response && error.response.status === 404) {
        if (state.certificationExamByVersionRequestToken !== requestToken) {
          return null
        }
        commit('STORE_CERTIFICATION_EXAM', null)
        return null
      }
      throw error
    }
  },
  setCertificationExamLocalAction({ commit }, exam = null) {
    commit('STORE_CERTIFICATION_EXAM', exam)
  },
  resetCertificationEditContextAction({ commit }) {
    commit(
      'SET_CERTIFICATION_VERSION_REQUEST',
      ++certificationVersionRequestToken
    )
    commit('SET_CERTIFICATION_EXAM_REQUEST', ++certificationExamRequestToken)
    commit(
      'SET_CERTIFICATION_EXAM_BY_VERSION_REQUEST',
      ++certificationExamByVersionRequestToken
    )
    commit('STORE_CERTIFICATION_VERSION', null)
    commit('STORE_CERTIFICATION_EXAM', null)
  },
  async getLaunchConfigurationAction({ commit, state }, params = {}) {
    const requestToken = ++launchConfigurationRequestToken
    commit('SET_LAUNCH_CONFIGURATION_REQUEST', requestToken)
    const response = await getLaunchConfiguration(params)
    if (state.launchConfigurationRequestToken !== requestToken) {
      return response
    }
    commit('STORE_LAUNCH_CONFIGURATION', response)
    return response
  },
  async updateLaunchConfigurationAction({ commit }, params = {}) {
    const response = await updateLaunchConfiguration(params)
    commit('STORE_LAUNCH_CONFIGURATION', response)
    return response
  },
  async updateLaunchSellabilityAction(_, params = {}) {
    const response = await updateLaunchSellability(params)
    return response
  },
}

export default actions
